




















// Vue
import Vue, { PropType } from "vue";
import { mapGetters } from "vuex";
// moment.js
import moment from "moment";
// jsPDF
import { jsPDF } from "jspdf";
// Constants
import { LOGO_BASE_64 } from "@/assets/media/logos/logo-base64";
// Models
import {
  JobApplicationModel,
  JobDriverModel,
  JobModel,
  DriverNoteModel,
  DeliveryDateType
} from "@/core/api/apiClient";

import mapPriceType from "@/core/helpers/enum_mappings/PriceType";
import mapDeliveryDateType from "@/core/helpers/enum_mappings/DeliveryDateType";
import mapJobType from "@/core/helpers/enum_mappings/JobType";
import mapQuantityType from "@/core/helpers/enum_mappings/QuantityType";

export default Vue.extend({
  name: "ExportJob",
  components: {},
  props: {
    job: Object as PropType<JobModel>
  },

  computed: {
    ...mapGetters({ currentUser: "currentUser" })
  },

  methods: {
    /**
     * Exports the Job as a PDF.
     * ? This function uses a tracked yValue that is used to build each "line-item" in the PDF.
     * ? If we're looking to add more sections, simply add or modify a function that explicitly
     * ? handles the feature/functionality, ensuring that you return the new yValue as you build out
     * ? each new section (email nickb@webility.ca for more information).
     * @author Nick Brahimir
     */
    exportJob(): void {
      const doc = new jsPDF();

      // Name of exported PDF file
      const fileName = `Job Report - ${this.job.customer?.name} - ${this.formatDateShort(
        this.job.deliveryDetails!.deliveryDate!
      )}.pdf`;

      let yValue = 10;

      yValue = this.pdf_BuildHeader(doc, yValue);
      yValue = this.insertNewLine(doc, yValue);

      // * Customer
      yValue = this.pdf_BuildCustomer(doc, yValue);
      yValue = this.insertNewLine(doc, yValue);

      // * Materials, Quantity, Applications
      yValue = this.pdf_BuildMaterialsQuantitiesApplications(doc, yValue);
      yValue = this.insertNewLine(doc, yValue);

      // * Jobsite Details
      yValue = this.pdf_BuildJobsiteDetails(doc, yValue);
      yValue = this.insertNewLine(doc, yValue);

      // * Drivers
      yValue = this.pdf_BuildDrivers(doc, yValue);

      // * Notes (Dispatcher, Driver, Additional)
      yValue = this.insertNewPage(doc);
      if (this.job.deliveryDetails?.notes) {
        yValue = this.pdf_BuildDispatcherNotes(doc, yValue, this.job.deliveryDetails.notes);
        yValue = this.insertNewLine(doc, yValue);
      }

      if (this.job.driverNotes?.length) {
        yValue = this.pdf_BuildDriverNotes(doc, yValue, this.job.driverNotes);
        yValue = this.insertNewLine(doc, yValue);
      }

      yValue = this.pdf_BuildAdditionalNotes(doc, yValue);

      // Download action
      doc.save(fileName);
    },

    pdf_BuildHeader(doc: jsPDF, yValue: number): number {
      // MD Title
      doc.setFontSize(18);
      doc.text(`${this.currentUser.client.name} Job Report`, 10, (yValue += 10));
      doc.setFontSize(12);

      // Logo
      doc.addImage(LOGO_BASE_64, "PNG", 150, 10, 50, 30);

      return yValue;
    },

    pdf_BuildCustomer(doc: jsPDF, yValue: number): number {
      doc.setFontSize(16);
      doc.text(
        `${this.job.customer?.name ? this.job.customer.name : "No Customer specified"}`,
        10,
        (yValue += 10)
      );
      doc.setFontSize(12);

      // Street Address
      doc.text(
        `Street address: ${
          this.job.jobSite?.streetAddress ? this.job.jobSite?.streetAddress : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // Customer Notes
      doc.text(
        `Customer notes: ${
          this.job.customer?.notes ? this.job.customer?.notes : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      yValue = this.insertNewLine(doc, yValue);

      // Delivery Date Type
      doc.text(
        `Delivery date type: ${
          this.job.deliveryDetails?.deliveryDateType
            ? mapDeliveryDateType(this.job.deliveryDetails.deliveryDateType)
            : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // Delivery Date
      doc.text(
        `Delivery date: ${
          this.job.deliveryDetails?.deliveryDate
            ? this.formatDate(this.job.deliveryDetails.deliveryDate)
            : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // Price
      doc.text(
        `Price: $${
          this.job.deliveryDetails?.price ? this.job.deliveryDetails?.price : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // Price Type
      doc.text(
        `Price type: ${
          this.job.deliveryDetails?.priceType
            ? mapPriceType(this.job.deliveryDetails.priceType)
            : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      return yValue;
    },

    pdf_BuildMaterialsQuantitiesApplications(doc: jsPDF, yValue: number): number {
      // Material
      if (!this.job.material?.material) doc.text("Material: ____________________", 10, (yValue += 10));
      if (this.job.material?.material) {
        doc.text(
          `Material: ${
            this.job.material.material.name ? this.job.material.material.name : "____________________"
          }`,
          10,
          (yValue += 10)
        );
      }

      // Quantity
      if (!this.job.quantity?.quantity) doc.text("Quantity: ____________________", 10, (yValue += 10));
      if (this.job.quantity?.quantity) {
        doc.text(
          `Quantity: ${
            this.job.quantity.quantity.name ? this.job.quantity.quantity.name : "____________________"
          } ${this.job.quantityType ? mapQuantityType(this.job.quantityType) : ""}`,
          10,
          (yValue += 10)
        );
      }

      // Applications
      doc.text(
        `Application(s): ${
          this.job.applications.length
            ? this.getStringApplications(this.job.applications)
            : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // Job Type
      doc.text(
        `Job type: ${this.job.jobType ? mapJobType(this.job.jobType) : "____________________"}`,
        10,
        (yValue += 10)
      );

      return yValue;
    },

    pdf_BuildJobsiteDetails(doc: jsPDF, yValue: number): number {
      // Unit
      doc.text(
        `Unit: ${
          this.job.jobSiteDetails?.unit ? this.job.jobSiteDetails?.unit : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // Block
      doc.text(
        `Block: ${
          this.job.jobSiteDetails?.block ? this.job.jobSiteDetails?.block : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // Municipal
      doc.text(
        `Municipal: ${
          this.job.jobSiteDetails?.municipal
            ? this.job.jobSiteDetails?.municipal
            : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      // PO
      doc.text(
        `PO: ${this.job.jobSiteDetails?.po ? this.job.jobSiteDetails?.po : "____________________"}`,
        10,
        (yValue += 10)
      );

      return yValue;
    },

    pdf_BuildDrivers(doc: jsPDF, yValue: number): number {
      doc.text(
        `Assigned Drivers: ${
          this.job.drivers?.length ? this.getStringDrivers(this.job.drivers) : "____________________"
        }`,
        10,
        (yValue += 10)
      );

      return yValue;
    },

    pdf_BuildAdditionalNotes(doc: jsPDF, yValue: number): number {
      doc.setFontSize(16);
      doc.text(`Additional Notes`, 10, (yValue += 10));
      doc.setFontSize(12);

      for (let index = 0; index < 10; index++) {
        yValue = this.insertLineSegment(doc, yValue);
      }

      return yValue;
    },

    pdf_BuildDispatcherNotes(doc: jsPDF, yValue: number, dispatcherNotes: string): number {
      if (!dispatcherNotes) return yValue;

      doc.setFontSize(16);
      doc.text(`Dispatcher Notes`, 10, (yValue += 10));
      doc.setFontSize(12);
      doc.text(`${dispatcherNotes}`, 10, (yValue += 10));

      return yValue;
    },

    pdf_BuildDriverNotes(doc: jsPDF, yValue: number, driverNotes: DriverNoteModel[]): number {
      if (!driverNotes) return yValue;
      if (!driverNotes.length) return yValue;

      doc.setFontSize(16);
      doc.text(`Driver Notes`, 10, (yValue += 10));
      doc.setFontSize(12);

      driverNotes.forEach((driverNote: DriverNoteModel) => {
        doc.setFont("Helvetica", "italic");
        doc.text(`${driverNote.driverName}`, 10, (yValue += 10));
        doc.setFontSize(10);
        doc.text(`${this.formatDate(driverNote.timestamp!, true)}`, 10, (yValue += 10));
        doc.setFontSize(12);
        doc.setFont("Helvetica", "normal");
        doc.text(`${driverNote.note}`, 10, (yValue += 10));
        yValue = this.insertNewLine(doc, yValue);
      });

      return yValue;
    },

    insertNewLine(doc: jsPDF, yValue: number): number {
      return yValue + 10;
    },

    insertLineSegment(doc: jsPDF, yValue: number): number {
      doc.line(10, yValue + 10, 195, yValue + 10);
      return yValue + 10;
    },

    insertNewPage(doc: jsPDF): number {
      doc.addPage();
      return 10;
    },

    /**
     * Return the DeliveryDateType (Not Confirmed, Will Call, Anytime) if it is specified on the Job. If
     * not, return the DeliveryDate time.
     * @author Nick Brahimir
     */
    getDeliveryDate(job: JobModel): string {
      if (!job.deliveryDetails?.deliveryDate) return "";

      const deliveryDateType = job.deliveryDetails.deliveryDateType;

      switch (DeliveryDateType[deliveryDateType]) {
        case DeliveryDateType.NotConfirmed:
          return DeliveryDateType.NotConfirmed;

        case DeliveryDateType.WillCall:
          return DeliveryDateType.WillCall;

        case DeliveryDateType.Anytime:
          return DeliveryDateType.Anytime;

        default:
          return this.formatDate(job.deliveryDetails.deliveryDate);
      }
    },

    /**
     * Converts an array of IApplications to a useable string to be printed to the PDF document.
     * @author Nick Brahimir
     */
    getStringApplications(data: JobApplicationModel[]): string {
      let result = "";
      data.forEach((application: JobApplicationModel) => {
        result += application.application?.name;

        const index = data.indexOf(application);
        if (index !== data.length - 1) result += ", ";
      });

      return result;
    },

    /**
     * Converts an array of IApplications to a useable string to be printed to the PDF document.
     * @author Nick Brahimir
     */
    getStringDrivers(data: JobDriverModel[]): string {
      let result = "";
      data.forEach((driver: JobDriverModel) => {
        result += `${driver.firstName} ${driver.lastName}`;

        const index = data.indexOf(driver);
        if (index !== data.length - 1) result += ", ";
      });

      return result;
    },

    convertUnix(date: any): string | undefined {
      if (!date.seconds) {
        return;
      }

      return moment(date.seconds * 1000)
        .toDate()
        .toISOString();
    },

    formatDate: function (date: string | Date, dayOfWeek?: boolean) {
      const convertedDate = this.convertUnix(date);
      if (!dayOfWeek) return moment(convertedDate).format("MMMM Do YYYY, h:mm A");
      return moment(convertedDate).format("dddd MMMM Do YYYY, h:mm A");
    },

    formatDateShort: function (date: string | Date) {
      const convertedDate = this.convertUnix(date);
      return moment(convertedDate).format("MMM D YYYY");
    }
  }
});
